import type { TransferData } from '../../constants'
import { cleanText, firstText, normalizeDate, normalizeDescriptionText, parseDescriptionFromHtml, readMeta } from '../../utils/parser'
import type { JsonObject } from '../../utils/jsonld'
import { readBookJsonLd, stringifyJsonLdValue } from '../../utils/jsonld'

function readDetailValue (labels: string[]): string {
  const candidates = Array.from(document.querySelectorAll<HTMLElement>('.bookitem-secondary-metadata li, li, tr, dl, div'))

  for (const row of candidates) {
    const text = cleanText(row.textContent || '')
    if (!text || text.length > 220) continue

    for (const label of labels) {
      const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`^${escapedLabel}\\s*[:：]\\s*(.+)$`, 'i')
      const match = text.match(regex)
      if (match?.[1]) return cleanText(match[1])
    }
  }

  return ''
}

function readSecondaryMetadataItems (): string[] {
  return Array.from(document.querySelectorAll<HTMLElement>('.bookitem-secondary-metadata li'))
    .map((item) => cleanText(item.textContent || ''))
    .filter(Boolean)
}

function readSecondaryMetadataValue (labels: string[]): string {
  for (const item of readSecondaryMetadataItems()) {
    const match = item.match(/^([^:：]+)\s*[:：]\s*(.+)$/)
    if (!match?.[1] || !match[2]) continue

    const label = cleanText(match[1]).toLowerCase()
    if (labels.some((candidate) => candidate.toLowerCase() === label)) return cleanText(match[2])
  }

  return ''
}

function readSecondaryMetadataPublisher (): string {
  const firstItem = readSecondaryMetadataItems()[0] || ''
  if (!firstItem || firstItem.includes('：') || firstItem.includes(':')) return ''
  return firstItem
}

function readSectionText (headingText: string): string {
  const headings = Array.from(document.querySelectorAll<HTMLElement>('h1, h2, h3, h4'))
  const heading = headings.find((el) => cleanText(el.textContent || '').toLowerCase() === headingText.toLowerCase())
  if (!heading) return ''

  const parts: string[] = []
  let current = heading.nextElementSibling as HTMLElement | null
  while (current && !/^H[1-4]$/i.test(current.tagName)) {
    const text = normalizeDescriptionText(current.textContent || '')
    if (text) parts.push(text)
    current = current.nextElementSibling as HTMLElement | null
  }

  return normalizeDescriptionText(parts.join('\n\n'))
}

function parseTitle (bookJsonLd: JsonObject | null): string {
  return firstText([
    '[data-testid="book-title"]',
    '.title-widget h1',
    '.product-title h1',
    'main h1',
    'h1',
    '[itemprop="name"]'
  ]) || stringifyJsonLdValue(bookJsonLd?.name) || readMeta('og:title') || 'book'
}

function parseAuthor (bookJsonLd: JsonObject | null): string {
  const selectors = [
    '[data-testid="author-name"] a',
    '[itemprop="author"]',
    '.contributors a[href*="/author/"]',
    '.author-name',
    'a[href*="/author/"]'
  ]

  const names = selectors
    .flatMap((selector) => Array.from(document.querySelectorAll<HTMLElement>(selector)))
    .map((el) => cleanText(el.textContent || ''))
    .filter((text) => text && !/^author$/i.test(text))

  if (names.length > 0) return Array.from(new Set(names)).join(', ')

  return stringifyJsonLdValue(bookJsonLd?.author)
}

function parsePublisher (bookJsonLd: JsonObject | null): string {
  const secondaryMetadataPublisher = readSecondaryMetadataPublisher()
  if (secondaryMetadataPublisher) return secondaryMetadataPublisher

  const explicit = readSecondaryMetadataValue(['Publisher', '出版社', 'レーベル']) || readDetailValue(['Publisher', '出版社', 'レーベル'])
  if (explicit) return explicit

  const publisher = stringifyJsonLdValue(bookJsonLd?.publisher)
  if (publisher) return publisher

  const detailsHeading = Array.from(document.querySelectorAll<HTMLElement>('h2, h3, h4'))
    .find((el) => /details/i.test(cleanText(el.textContent || '')))
  const firstDetail = detailsHeading?.nextElementSibling?.querySelector<HTMLElement>('li')
  const text = cleanText(firstDetail?.textContent || '')
  return text && !text.includes(':') ? text : ''
}

function parseIsbn (bookJsonLd: JsonObject | null): string {
  const fromDetails = readSecondaryMetadataValue(['Book ID', '書籍ID', 'ISBN', 'ISBN-13', 'ISBN-10']) || readDetailValue(['Book ID', '書籍ID', 'ISBN', 'ISBN-13', 'ISBN-10'])
  const fromJson = stringifyJsonLdValue(bookJsonLd?.isbn)
  return (fromDetails || fromJson).replace(/[^0-9X]/gi, '')
}

function parsePages (bookJsonLd: JsonObject | null): string {
  const numberOfPages = stringifyJsonLdValue(bookJsonLd?.numberOfPages)
  const fromJson = numberOfPages.match(/\b(\d{1,5})\b/)?.[1]
  if (fromJson) return fromJson

  const about = readSectionText('About this book')
  const fromAbout = about.match(/\b(\d{1,5})\s+pages?\b/i)?.[1]
  if (fromAbout) return fromAbout

  const text = document.body?.innerText || ''
  return text.match(/\b(\d{1,5})\s+pages?\b/i)?.[1] || ''
}

function parsePublicationDate (bookJsonLd: JsonObject | null): string {
  const release = readSecondaryMetadataValue(['Release Date', '発売日', 'Publication Date', 'Published', '出版日']) || readDetailValue(['Release Date', '発売日', 'Publication Date', 'Published', '出版日'])
  if (release) return normalizeDate(release)

  const fromJson = stringifyJsonLdValue(bookJsonLd?.datePublished)
  return fromJson ? normalizeDate(fromJson) : ''
}

function parseLanguage (bookJsonLd: JsonObject | null): string {
  return readSecondaryMetadataValue(['Language', '言語']) || readDetailValue(['Language', '言語']) || stringifyJsonLdValue(bookJsonLd?.inLanguage)
}

function parseDescription (bookJsonLd: JsonObject | null): string {
  const fullSynopsis = document.querySelector<HTMLElement>('[data-full-synopsis]')
  if (fullSynopsis) {
    const value = parseDescriptionFromHtml(fullSynopsis.innerHTML)
    if (value) return value
  }

  const selectors = [
    '[data-testid="synopsis"]',
    '[itemprop="description"]',
    '#synopsis',
    '.synopsis-description',
    '.bookitem-synopsis',
    '.description'
  ]

  for (const selector of selectors) {
    const el = document.querySelector<HTMLElement>(selector)
    if (!el) continue

    const value = parseDescriptionFromHtml(el.innerHTML)
    if (value && !/^expand\/collapse synopsis$/i.test(value)) return value.replace(/^Expand\/Collapse Synopsis\s*/i, '').trim()
  }

  const fromSection = readSectionText('Synopsis').replace(/^Expand\/Collapse Synopsis\s*/i, '').trim()
  if (fromSection) return fromSection

  return normalizeDescriptionText(stringifyJsonLdValue(bookJsonLd?.description) || readMeta('description', 'og:description'))
}

function sanitizeKoboCoverUrl (url: string): string {
  if (!url) return ''

  const sanitized = url.replace(/(\/book-images\/[^/]+)\/\d+\/\d+\/\d+\/(?:True|False)\//i, '$1/')
  if (sanitized.startsWith('//')) return `${window.location.protocol}${sanitized}`
  return sanitized
}

function parseCoverUrl (bookJsonLd: JsonObject | null): string {
  return sanitizeKoboCoverUrl(
    stringifyJsonLdValue(bookJsonLd?.image) ||
    document.querySelector<HTMLImageElement>('[data-testid="cover-image"], img.cover-image, .cover-image img, img[alt*="ebook"], img[alt*="audiobook"]')?.src ||
    readMeta('og:image')
  )
}

function parseFormat (): string {
  const value = `${window.location.pathname} ${firstText(['[data-testid="format"]', '.format', '.book-format'])} ${document.body?.innerText.slice(0, 2000) || ''}`.toLowerCase()
  if (value.includes('audiobook')) return 'audio'
  return 'digital'
}

export function parseKoboPage (): TransferData {
  const bookJsonLd = readBookJsonLd()

  return {
    title: parseTitle(bookJsonLd),
    originalTitle: '',
    author: parseAuthor(bookJsonLd),
    language: parseLanguage(bookJsonLd),
    publisher: parsePublisher(bookJsonLd),
    isbn: parseIsbn(bookJsonLd),
    pages: parsePages(bookJsonLd),
    pubDate: parsePublicationDate(bookJsonLd),
    format: parseFormat(),
    desc: parseDescription(bookJsonLd),
    rating: '',
    review: '',
    coverUrl: parseCoverUrl(bookJsonLd)
  }
}
